<template>
  <Modal :open="open" @close="$emit('close')" size="big" :showCloseButton="false">
    <div class="flex flex-col">
      <!-- Header -->
      <div
        class="bg-linear-to-r/srgb from-purple-500 to-purple-600 rounded-t-lg px-6 py-6 -mx-4 -mt-5 sm:-mx-8 sm:-mt-8"
      >
        <div class="flex items-start justify-between">
          <div>
            <h2 class="text-xl font-bold text-white">{{ t("header_title") }}</h2>
            <p class="mt-1 text-purple-100 text-sm">{{ t("header_subtitle") }}</p>
          </div>
          <button @click="$emit('close')" class="hover:text-white transition-colors rounded-full p-1">
            <XIcon class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- Alert -->
      <div class="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
        <ExclamationIcon class="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
        <p class="text-sm text-yellow-800">{{ t("alert_message") }}</p>
      </div>

      <!-- Training guides banner -->
      <router-link
        to="/pos/training"
        @click="$emit('close')"
        class="mt-4 flex items-center justify-between gap-3 rounded-lg border border-purple-200 bg-purple-50 px-5 py-4 hover:bg-purple-100 transition-colors group"
      >
        <div class="flex items-center gap-3">
          <BookOpenIcon class="h-5 w-5 text-purple-600 shrink-0" />
          <p class="text-sm font-medium text-purple-800">{{ t("training_guides_banner") }}</p>
        </div>
        <ArrowRightIcon class="h-4 w-4 text-purple-400 group-hover:text-purple-600 transition-colors shrink-0" />
      </router-link>

      <!-- Steps -->
      <div class="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div v-for="step in steps" :key="step.number" class="border border-gray-200 rounded-lg p-4">
          <div class="flex items-center gap-3 mb-2">
            <span
              class="flex items-center justify-center h-7 w-7 rounded-full text-white text-sm font-bold shrink-0"
              :class="step.number === 6 ? 'bg-green-500' : 'bg-purple-500'"
            >
              {{ step.number }}
            </span>
            <h3 class="font-semibold text-gray-900 text-sm">{{ t(step.titleKey) }}</h3>
          </div>
          <p class="text-sm text-gray-600 ml-10">{{ t(step.descriptionKey) }}</p>
          <a
            v-if="step.link"
            :href="step.link"
            target="_blank"
            class="text-sm text-purple-600 hover:underline ml-10 mt-1 inline-block"
          >
            {{ step.link }}
          </a>
        </div>
      </div>

      <!-- Reminders -->
      <div class="mt-6 flex flex-wrap gap-2">
        <span
          v-for="(reminderKey, index) in reminderKeys"
          :key="index"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium"
        >
          <CheckCircleIcon class="h-4 w-4" />
          {{ t(reminderKey) }}
        </span>
      </div>

      <!-- Footer -->
      <div class="mt-6 border-t border-gray-200 pt-4">
        <p class="text-xs text-gray-500 text-center">{{ t("footer_contact") }}</p>
      </div>
    </div>
  </Modal>
</template>

<script>
import { Modal } from "@estaly/ui";
import { XIcon, BookOpenIcon, ArrowRightIcon } from "@heroicons/vue/outline";
import { ExclamationIcon, CheckCircleIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";

export default {
  components: {
    Modal,
    XIcon,
    BookOpenIcon,
    ArrowRightIcon,
    ExclamationIcon,
    CheckCircleIcon,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["close"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      steps: [
        { number: 1, titleKey: "step_1_title", descriptionKey: "step_1_description", link: "https://but.estaly.co" },
        { number: 2, titleKey: "step_2_title", descriptionKey: "step_2_description" },
        { number: 3, titleKey: "step_3_title", descriptionKey: "step_3_description" },
        { number: 4, titleKey: "step_4_title", descriptionKey: "step_4_description" },
        { number: 5, titleKey: "step_5_title", descriptionKey: "step_5_description" },
      ],
      reminderKeys: ["reminder_no_manual_entry", "reminder_auto_sync", "reminder_dashboard_tracking"],
    };
  },
};
</script>

<i18n src="./ExternalPosSubscriptionGuideModal.i18n.json"></i18n>
